import { gql, useMutation } from '@apollo/client';
import { useEffect, useCallback } from 'react';
import { MapBoxTrackingTime, MapBoxPageMarker } from '../../constants/mapBoxTrackLog';
import { detectPlatform, MapBoxPlatform } from '../../utils/deviceUtils';


/**
 * Mapbox 地图使用情况打点 GraphQL Mutation
 * 用于记录用户对地图功能的使用情况，便于分析地图使用频率和用户行为
 */
export const MAP_BOX_TRACK_LOG = gql`
  mutation MapBoxTrackLog($input: MapBoxTrackLogInput!) {
    mapBoxTrackLog(input: $input)
  }
`;

/**
 * GraphQL Mutation 输入接口
 * 定义发送到服务端的打点数据结构
 */
export interface MapBoxTrackLogInput {
  /** 页面标识符，标识具体的页面类型 */
  pageMarker: string;
  /** 打点时机，目前只有地图加载时打点 */
  trackingTime?: string | null;
  /** 平台类型：web, ios, android */
  platform?: string | null;
  /** 客户端时间戳（毫秒），用于记录用户本地时间 */
  clientTimestamp?: number | null;
}

/**
 * GraphQL Variables 接口
 * Apollo Client 使用的变量结构
 */
interface MapBoxTrackLogVariables {
  input: MapBoxTrackLogInput;
}

/**
 * 内部使用的打点选项接口
 * 提供更强的类型安全性和开发体验
 */
interface MapBoxTrackLogOptions {
  /** 页面标识符 */
  pageMarker: string;
  /** 打点时机（强类型） */
  trackingTime: MapBoxTrackingTime;
  /** 平台类型 */
  platform?: string;
  /** 客户端时间戳 */
  clientTimestamp?: number | null;
}

/**
 * Mapbox 地图打点 Hook
 *
 * 提供地图使用情况打点功能，支持页面加载打点
 *
 * @returns log 函数，用于发送打点数据
 */
export const useMapBoxTrackLog = () => {
  const [mapBoxTrackLogMutation] = useMutation<void, MapBoxTrackLogVariables>(MAP_BOX_TRACK_LOG);

  /**
   * 发送打点数据的核心函数
   *
   * @param options 打点选项，包含页面信息、视图模式、打点时机等
   * @returns Promise<void> 异步发送打点数据
   */
  const log = useCallback(async (options: MapBoxTrackLogOptions) => {
    try {
      await mapBoxTrackLogMutation({
        variables: {
          input: {
            pageMarker: options.pageMarker,
            trackingTime: options.trackingTime || MapBoxTrackingTime.MAP_LOAD,
            platform: options.platform || MapBoxPlatform.UNKNOWN,
            clientTimestamp: options.clientTimestamp
          }
        }
      });
    } catch (error) {
      // 静默失败，不影响用户体验
    }
  }, [mapBoxTrackLogMutation]);

  return log;
};

/**
 * 自动地图加载打点 Hook
 *
 * 在地图页面加载时自动发送打点数据，记录页面标识符、来源页面和平台信息
 *
 * @param pageMarker 页面标识符
 */
export const useMapBoxPageLoadTrack = (pageMarker: string | null | undefined) => {
  const log = useMapBoxTrackLog();

  useEffect(() => {
    // 如果 pageMarker 为空，使用默认值，确保不会漏打点
    const finalPageMarker = pageMarker || MapBoxPageMarker.ERROR_PAGE;

    try {
      // 发送打点数据 - 每次组件挂载或 pageMarker 变化时都打点
      log({
        pageMarker: finalPageMarker,
        trackingTime: MapBoxTrackingTime.MAP_LOAD,
        platform: detectPlatform(),
        clientTimestamp: Date.now()
      });
    } catch (error) {
      // 如果处理过程中出现异常，尝试发送基本的打点数据，确保后端有日志记录
      try {
        log({
          pageMarker: MapBoxPageMarker.ERROR_PAGE,
          trackingTime: MapBoxTrackingTime.MAP_LOAD,
          platform: MapBoxPlatform.UNKNOWN,
          clientTimestamp: Date.now()
        });
      } catch (logError) {
        // 静默失败，避免影响用户体验
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMarker]); // 每次 pageMarker 变化或组件挂载时都打点
};


